import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Badge,
} from "../../components/ui";
import { Seo } from "../../lib/head";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";

interface Sub {
  name: string;
  purpose: string;
  data_categories: string;
  location: string;
  // "Not required — de-identified" is deliberately its own value. Forcing the
  // Anthropic row into "N/A — no PHI" would read as "we never send anything
  // sensitive"; forcing it into "Signed" would assert a BAA that does not
  // exist. The honest statement is that no BAA covers it AND no PHI reaches it.
  baa: "Signed" | "N/A — no PHI" | "On request" | "Not required — de-identified";
}

// AWS is the ONLY subprocessor that receives PHI, and therefore the only one we
// hold a BAA with. That is the entire point of de-identifying at the AWS edge:
// everything downstream gets Safe Harbor tokens, so no other agreement is
// required or exists. Any row claiming "Signed" other than the AWS rows is a
// false statement about a contract — which this table made three times
// (Supabase in c15ea73, then Resend and Render here). Subprocessors.test.tsx
// now asserts the general rule rather than checking rows one at a time, which
// is how the second and third instances survived the first correction.
const SUBS: Sub[] = [
  // PHI is received into AWS and de-identified per 45 C.F.R. 164.514 BEFORE it
  // reaches any downstream service; Supabase holds Safe Harbor tokens only and
  // re-identification happens back inside AWS, where the keys live. This row
  // previously read "Customer content (incl. PHI)" with baa "Signed" — both
  // untrue, and the second is the kind of claim a customer relies on. It is
  // also exactly what the comment on the `baa` type below forbids.
  { name: "Supabase", purpose: "Primary application database and file storage — de-identified data only.", data_categories: "Safe Harbor tokens (residents, facilities, prescribers), audit log (counts and IDs, PHI-free), staff account data. No direct identifiers.", location: "US", baa: "Not required — de-identified" },
  // This row read "AI inference on de-identified prompts", describing Bedrock.
  // Bedrock was never provisioned — the AWS application was declined — so the
  // row named a service that does no work while AWS's ACTUAL role in the PHI
  // boundary had no row at all: the Lambdas that de-identify on ingest and
  // re-identify at render (infra/lib/phi-stack.ts:269,313), and the DynamoDB
  // re-identification map (line 113) that is the only place real resident
  // identities exist. That map is the single most consequential thing any
  // subprocessor holds, and this register did not mention it.
  { name: "Amazon Web Services (Lambda + DynamoDB)", purpose: "De-identification on ingest and re-identification at render — the compute inside the PHI boundary.", data_categories: "PHI: the re-identification map linking Safe Harbor tokens to real resident identities, and charts in flight through de-identification. Encrypted with a customer-managed KMS key.", location: "US (us-east-2)", baa: "Signed" },
  // "Failover when the primary provider is throttled" was written when Bedrock
  // was expected to be primary. It never was, so Anthropic serves EVERY
  // inference call rather than occasional overflow. Understating a
  // subprocessor's role on the page a compliance officer reads is the wrong
  // direction to be wrong in. The BAA status is unchanged either way: what
  // makes it "not required" is the de-identification verified at egress, not
  // how often the vendor is called.
  { name: "Anthropic", purpose: "AI inference for recommendation drafting and assistive summaries — the primary and sole inference provider.", data_categories: "De-identified prompts only (Safe Harbor), verified at egress", location: "US", baa: "Not required — de-identified" },
  // The PHI buckets from the CDK stack (infra/lib/phi-stack.ts:92-93): `ingests`
  // holds charts exactly as uploaded — before de-identification runs — and
  // `letters` holds PDFs the render Lambda has RE-identified (it is granted
  // reid read access at line 404). Both are PHI, and saying so is the entire
  // purpose of this table.
  //
  // This row previously read "Encrypted off-site backup of production data" /
  // "Customer content (encrypted at rest)", wrong in both directions: it claimed
  // a backup that no cron, handler or script in this repository performs, and it
  // concealed that these buckets are where PHI actually lives. Understating a
  // data category is the worse half — a customer reads this page to find out
  // where their residents' data goes, not to be reassured. The missing backup is
  // tracked as A1-5 in docs/policies/business-continuity-and-dr.md; it is a gap
  // to build, not a sentence to keep.
  { name: "Amazon Web Services (S3)", purpose: "Raw chart ingest and rendered letter artifacts — the storage layer inside the PHI boundary.", data_categories: "PHI: charts as uploaded (pre-de-identification) and rendered letters carrying resident identifiers. Encrypted with a customer-managed KMS key; every object access logged to CloudTrail.", location: "US (us-east-2)", baa: "Signed" },
  // A SEPARATE bucket from the two above, and a separate row because it holds a
  // different thing: a daily copy of the Supabase database and Storage objects.
  //
  // This row was DELETED on 2026-08-03 because the page claimed an off-site
  // backup that no code performed, and restored later the same day only after a
  // run actually succeeded — 222 tables / 356,347 rows / 26 Storage objects,
  // manifest stamped at migration 20260803114000. The ordering is the point: a
  // trust-center claim follows the verified behavior, never the merged code.
  { name: "Amazon Web Services (S3 — off-site backup)", purpose: "Daily backup of the application database and stored files, held outside the primary provider.", data_categories: "Complete copy of the Supabase database (Safe Harbor tokens, staff account records) and Storage objects. Customer-managed KMS key, versioned, Object-Locked, seven-year retention.", location: "US (us-east-2)", baa: "Signed" },
  { name: "Intuit QuickBooks Online", purpose: "Invoice drafting from time entries and bundle outcomes.", data_categories: "Facility-level billing metadata. No PHI on invoice line items.", location: "US", baa: "N/A — no PHI" },
  { name: "Google Cloud (Calendar API)", purpose: "Two-way calendar sync via OAuth.", data_categories: "Calendar event titles/times. No PHI in event body.", location: "US", baa: "On request" },
  { name: "Microsoft (Outlook/Graph API)", purpose: "Two-way calendar sync via OAuth.", data_categories: "Calendar event titles/times. No PHI in event body.", location: "US", baa: "On request" },
  { name: "Resend", purpose: "Transactional and opt-in marketing email.", data_categories: "Recipient email, subject line, sanitized body. No PHI.", location: "US", baa: "N/A — no PHI" },
  { name: "Twilio", purpose: "SMS and RCS reminders (opt-in only).", data_categories: "Recipient phone, sanitized message body and media (facility/count only — no PHI, enforced by template whitelist).", location: "US", baa: "N/A — no PHI" },
  { name: "Render", purpose: "Application hosting — de-identified compute only.", data_categories: "Application logs (no PHI). HTTP request metadata.", location: "US", baa: "N/A — no PHI" },
  { name: "Cloudflare", purpose: "DNS, edge caching, DDoS protection.", data_categories: "HTTP request metadata only. No PHI.", location: "Global CDN", baa: "N/A — no PHI" },
];

export function Subprocessors() {
  return (
    <>
      <Seo title="Subprocessors — WeConsult Rx" description="WeConsult Rx subprocessors: Supabase, AWS, Anthropic, QuickBooks Online, Google, Microsoft, Resend, Twilio, Render, Cloudflare. BAA status per processor." path="/subprocessors" />
    <Section>
      <Container>
        <Link
          to="/trust"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Trust center
        </Link>
        <div className="mt-6 max-w-3xl">
          <Eyebrow>Trust center</Eyebrow>
          <Heading level={1} className="mt-3">
            Subprocessors
          </Heading>
          <Lede className="mt-6">
            Every third party that may process customer data. We notify
            customers at least 30 days before engaging a new subprocessor for
            any covered data category.
          </Lede>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-ink-700">
            <p>
              <span className="font-semibold text-ink-900">Electronic signature is not on this
              list.</span>{" "}
              Signing happens inside the platform. A signature is recorded with a timestamped
              attestation and a hash of the document that was signed, and for recommendations
              carrying PHI the signing record is written in our own environment. No third party
              receives the signed document or the signer&rsquo;s identity.
            </p>
            <p>
              <span className="font-semibold text-ink-900">AI inference never receives
              PHI.</span>{" "}
              Prompts are de-identified to the HIPAA Safe Harbor standard before they leave our
              environment, and the de-identification is verified at the egress boundary — a prompt
              containing residual identifiers is blocked rather than sent. Our primary inference
              provider operates under a signed BAA. Anthropic is used only as failover when that
              provider is throttled or unavailable, and is listed here because it may receive
              de-identified prompts, not because it receives PHI.
            </p>
          </div>
        </div>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-canvas">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold text-ink-700">Subprocessor</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Purpose</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Data categories</th>
                <th className="px-5 py-3 font-semibold text-ink-700">Location</th>
                <th className="px-5 py-3 font-semibold text-ink-700">BAA</th>
              </tr>
            </thead>
            <tbody>
              {SUBS.map((s, i) => (
                <tr key={s.name} className={i % 2 === 0 ? "bg-canvas/30" : ""}>
                  <td className="px-5 py-4 font-semibold text-ink-900 align-top">{s.name}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.purpose}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.data_categories}</td>
                  <td className="px-5 py-4 text-ink-700 align-top">{s.location}</td>
                  <td className="px-5 py-4 align-top">
                    <Badge tone={s.baa === "Signed" ? "forest" : "neutral"}>{s.baa}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-sm text-ink-500">
          Last updated 2026-05-30. Subscribe to subprocessor changes by emailing privacy@weconsultrx.com.
        </p>
      </Container>
    </Section>
    </>
  );
}
